import React, { useEffect, useState } from 'react';
import { ShoppingCart, Package, Clock, CheckCircle, XCircle, Download } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AnalyticsAPI, OrdersAnalytics } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';

export default function OrdersAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<OrdersAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AnalyticsAPI.getOrdersAnalytics({ period });
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching orders analytics:', err);
      setError(err.message || 'Failed to load orders analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading orders analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Failed to load analytics'}</p>
          <Button onClick={fetchAnalytics}>Retry</Button>
        </div>
      </div>
    );
  }

  const { data } = analytics;
  const statusEntries = Object.entries(data.statusDistribution);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Orders Analytics</h1>
          <p className="text-muted-foreground">Comprehensive order insights and trends</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <select 
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value as any)}
          >
            <option value="day">Last 24 hours</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="year">Last year</option>
          </select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Total Orders</p>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{data.totalOrders}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Avg Order Value</p>
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold">{formatCurrency(data.avgOrderValue)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Pending</p>
              <Clock className="h-4 w-4 text-yellow-600" />
            </div>
            <p className="text-2xl font-bold">{data.statusDistribution.pending || 0}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Delivered</p>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-2xl font-bold">{data.statusDistribution.delivered || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Status distribution */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Order Status Distribution</CardTitle>
            <CardDescription>Breakdown by order status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {statusEntries.map(([status, count]) => {
                const percentage = data.totalOrders > 0 ? (count / data.totalOrders) * 100 : 0;
                const colors: Record<string, string> = {
                  pending: 'bg-yellow-500',
                  confirmed: 'bg-blue-500',
                  processing: 'bg-indigo-500',
                  delivered: 'bg-green-500',
                  cancelled: 'bg-red-500',
                };
                
                return (
                  <div key={status}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium capitalize">{status}</span>
                      <span className="text-sm text-muted-foreground">
                        {count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className={`rounded-full h-2 ${colors[status] || 'bg-gray-500'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Order Status Summary</CardTitle>
            <CardDescription>Visual representation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {statusEntries.map(([status, count]) => {
                const icons: Record<string, any> = {
                  pending: Clock,
                  confirmed: CheckCircle,
                  processing: Package,
                  delivered: CheckCircle,
                  cancelled: XCircle,
                };
                const colors: Record<string, string> = {
                  pending: 'text-yellow-600 bg-yellow-100',
                  confirmed: 'text-blue-600 bg-blue-100',
                  processing: 'text-indigo-600 bg-indigo-100',
                  delivered: 'text-green-600 bg-green-100',
                  cancelled: 'text-red-600 bg-red-100',
                };
                const Icon = icons[status] || Package;
                
                return (
                  <div key={status} className="rounded-lg border p-4">
                    <div className={`h-10 w-10 rounded-full ${colors[status] || 'bg-gray-100'} flex items-center justify-center mb-2`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="text-2xl font-bold">{count}</div>
                    <div className="text-xs text-muted-foreground capitalize">{status}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders per day chart */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Orders</CardTitle>
          <CardDescription>Order count and value by day</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[350px]">
            {data.ordersPerDay.length > 0 ? (
              <div className="flex flex-col h-full">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex gap-4">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-primary mr-2"></div>
                      <span className="text-sm">Order Count</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex-1 flex items-end gap-1">
                  {data.ordersPerDay.slice(-15).map((day) => {
                    const maxCount = Math.max(...data.ordersPerDay.map(d => d.count));
                    const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                    const date = new Date(day.date);
                    const label = `${date.getMonth() + 1}/${date.getDate()}`;
                    
                    return (
                      <div key={day.date} className="relative flex-1 flex flex-col items-center group h-[270px]">
                        <div
                          className="w-full bg-primary rounded-t-sm transition-all hover:opacity-80"
                          style={{ height: `${height}%` }}
                        ></div>
                        <span className="mt-2 text-xs">{label}</span>
                        <div className="absolute -top-16 hidden group-hover:block bg-popover text-popover-foreground px-3 py-2 rounded text-xs whitespace-nowrap shadow-lg border z-10">
                          <div className="font-medium">{date.toLocaleDateString()}</div>
                          <div className="text-primary">{day.count} orders</div>
                          <div className="text-muted-foreground">{formatCurrency(day.total)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No order data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
